import React from 'react';
import { Target, Coffee, Check } from 'lucide-react';

/**
 * Компонент с секцией целей по весу и настройками питания
 */
const GoalSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  dietTypes 
}) => {
  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Target className="w-5 h-5 mr-2 text-purple-500" />
        Цели и настройки питания
      </h3>
      
      {/* Выбор цели */}
      <div className="mb-4">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Ваша цель
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className={`inline-flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-colors 
            ${formData.goal === 'lose' 
              ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/40 dark:border-purple-700 dark:text-purple-300'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}>
            <input
              type="radio"
              name="goal"
              value="lose"
              checked={formData.goal === 'lose'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col items-center">
              <span className="text-xl mb-1">⬇️</span>
              <span className="font-medium">Снижение веса</span>
            </div>
          </label>
          <label className={`inline-flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-colors 
            ${formData.goal === 'maintain' 
              ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/40 dark:border-purple-700 dark:text-purple-300'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}>
            <input
              type="radio"
              name="goal"
              value="maintain"
              checked={formData.goal === 'maintain'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col items-center">
              <span className="text-xl mb-1">⚖️</span>
              <span className="font-medium">Поддержание</span>
            </div>
          </label>
          <label className={`inline-flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-colors 
            ${formData.goal === 'gain' 
              ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/40 dark:border-purple-700 dark:text-purple-300'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}>
            <input
              type="radio"
              name="goal"
              value="gain"
              checked={formData.goal === 'gain'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col items-center">
              <span className="text-xl mb-1">⬆️</span>
              <span className="font-medium">Набор массы</span>
            </div>
          </label>
        </div>
        {errors.goal && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.goal}
          </p>
        )}
      </div>
      
      {/* Скорость изменения веса (если цель не "поддержание") */}
      {(formData.goal === 'lose' || formData.goal === 'gain') && (
        <div className="mb-4">
          <label htmlFor="weightGoal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {formData.goal === 'lose' ? 'Темп снижения веса (кг в неделю)' : 'Темп набора массы (кг в неделю)'}
          </label>
          <select
            id="weightGoal"
            name="weightGoal"
            value={formData.weightGoal}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.weightGoal ? 'input-error' : ''}`}
          >
            <option value="0.25">0.25 кг в неделю (медленно)</option>
            <option value="0.5">0.5 кг в неделю (оптимально)</option>
            <option value="0.75">0.75 кг в неделю (быстро)</option>
            <option value="1">1 кг в неделю (очень быстро)</option>
            {formData.goal === 'lose' && (
              <>
                <option value="1.5">1.5 кг в неделю (экстремально)</option>
                <option value="2">2 кг в неделю (не рекомендуется)</option>
              </>
            )}
          </select>
          {errors.weightGoal && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.weightGoal}
            </p>
          )}
          <p className="mt-1 text-xs text-purple-600 dark:text-purple-400">
            {formData.goal === 'lose' 
              ? 'Рекомендуемый темп снижения веса — 0.5-1 кг в неделю. Более быстрое снижение может быть вредным для здоровья.'
              : 'Рекомендуемый темп набора мышечной массы — 0.25-0.5 кг в неделю. Более быстрый набор может привести к избыточному накоплению жира.'
            }
          </p>
        </div>
      )}
      
      {/* Тип питания */}
      <div className="mb-4">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <Coffee className="w-4 h-4 mr-2 text-purple-500" />
          Тип питания
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {dietTypes.map((diet) => (
            <label 
              key={diet.id}
              className={`flex items-start p-3 rounded-lg border transition-colors cursor-pointer
                ${formData.dietType === diet.id 
                  ? 'bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-900/40 dark:border-purple-700 dark:text-purple-200'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
              <input
                type="radio"
                name="dietType"
                value={diet.id}
                checked={formData.dietType === diet.id}
                onChange={handleInputChange}
                className="form-radio mt-1"
              />
              <div className="ml-3">
                <div className="flex justify-between">
                  <span className="block font-medium text-gray-900 dark:text-white">
                    {diet.name}
                  </span>
                  <span className="text-xs text-purple-600 dark:text-purple-400">
                    {diet.macro.protein * 100}% / {diet.macro.fat * 100}% / {diet.macro.carbs * 100}%
                  </span>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {diet.description}
                </span>
              </div>
            </label>
          ))}
        </div>
        {errors.dietType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.dietType}
          </p>
        )}
      </div>
      
      {/* Количество приемов пищи */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-1">
        <div>
          <label htmlFor="mealCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Количество приемов пищи в день
          </label>
          <select
            id="mealCount"
            name="mealCount"
            value={formData.mealCount}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.mealCount ? 'input-error' : ''}`}
          >
            <option value="1">1 прием пищи</option>
            <option value="2">2 приема пищи</option>
            <option value="3">3 приема пищи</option>
            <option value="4">4 приема пищи</option>
            <option value="5">5 приемов пищи</option>
            <option value="6">6 приемов пищи</option>
          </select>
          {errors.mealCount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.mealCount}
            </p>
          )}
        </div>
        
        <div>
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mt-1 h-5">&nbsp;</div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includeSnacks"
              checked={formData.includeSnacks}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Включить перекусы в план питания
            </span>
          </label>
        </div>
      </div>
      
      <div className="mt-4 bg-white dark:bg-gray-800 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
        <div className="flex items-start">
          <Check className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
              Распределение белков, жиров, углеводов (БЖУ)
            </h4>
            <p className="text-xs text-purple-600 dark:text-purple-400">
              Выбранный тип питания определяет распределение макронутриентов в вашем рационе.
              Для большинства людей подходит сбалансированное питание с равномерным распределением БЖУ.
              При снижении веса может быть эффективна низкоуглеводная диета, а при наборе мышечной массы
              — высокобелковая.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalSection;